/**
 * Centralized processing queue utilities
 * Handles workflow processing queue management
 * 
 * This utility can be used from components via useStore hook or by passing store as parameter
 * For direct access outside React components, import store directly
 */

import { 
  addToProcessingQueue, 
  removeFromProcessingQueue, 
  setCurrentlyProcessingSession,
  updateWorkflowSessionState
} from '../store/reducers';

// Import store directly - components using this utility can also pass their own store instance
let storeInstance = null;

/**
 * Set the store instance (called from Dashboard or other components)
 * @param {Object} store - Redux store instance
 */
export const setStoreInstance = (store) => {
  storeInstance = store;
};

/**
 * Get the store instance
 * @returns {Object} Redux store instance
 */
const getStore = () => {
  if (!storeInstance) {
    // Fallback: try to import store directly (works for non-React contexts)
    try {
      const storeModule = require('../store/store');
      storeInstance = storeModule.default || storeModule;
    } catch (e) {
      console.error('ProcessingQueueUtils: Could not get store instance. Make sure to call setStoreInstance() first.');
      throw new Error('Store instance not available');
    }
  }
  return storeInstance;
};

/**
 * Check if a workflow can process (no other workflow is processing)
 * @param {string} workflowSessionId - The workflow session ID to check
 * @returns {boolean} True if workflow can process, false otherwise
 */
export const canWorkflowProcess = (workflowSessionId) => {
  const store = getStore();
  const state = store.getState();
  const workflowSessions = state?.workflowSessions || {};
  const currentlyProcessingId = workflowSessions?.currentlyProcessingSessionId;
  
  // Can process if nothing is processing, or this workflow is the one processing
  return !currentlyProcessingId || currentlyProcessingId === workflowSessionId;
};

/**
 * Check if another workflow is currently processing
 * @param {string} workflowSessionId - The workflow session ID to check
 * @returns {boolean} Always returns false - backend now supports multiple concurrent jobs
 * @deprecated Backend now supports multiple concurrent jobs, no need to check
 */
export const isAnotherWorkflowProcessing = (workflowSessionId) => {
  // Backend now supports multiple concurrent jobs - all workflows can process simultaneously
  return false;
};

/**
 * Add workflow to processing queue if another workflow is processing
 * @param {string} workflowSessionId - The workflow session ID to add to queue
 * @returns {Object} { addedToQueue: boolean, message: string }
 */
export const addToQueueIfNeeded = (workflowSessionId) => {
  // Backend now supports multiple concurrent jobs - workflows no longer need queuing
  return {
    addedToQueue: false,
    message: 'Workflow can process immediately',
  };
};

/**
 * Get queue information for a workflow
 * @param {string} workflowSessionId - The workflow session ID
 * @returns {Object} { position: number, totalInQueue: number, isCurrentlyProcessing: boolean }
 */
export const getQueueInfo = (workflowSessionId) => {
  const store = getStore();
  const state = store.getState();
  const workflowSessions = state?.workflowSessions || {};
  const currentlyProcessingId = workflowSessions?.currentlyProcessingSessionId;
  const queue = workflowSessions?.processingQueue || [];
  
  const isCurrentlyProcessing = currentlyProcessingId === workflowSessionId;
  
  if (isCurrentlyProcessing) {
    return {
      position: 1,
      totalInQueue: (currentlyProcessingId ? 1 : 0) + queue.length,
      isCurrentlyProcessing: true,
    };
  }
  
  const queuePosition = queue.indexOf(workflowSessionId);
  
  if (queuePosition === -1) {
    return {
      position: null,
      totalInQueue: (currentlyProcessingId ? 1 : 0) + queue.length,
      isCurrentlyProcessing: false,
    };
  }
  
  // Position is queue index + 2 (because position 1 is currently processing)
  return {
    position: queuePosition + 2,
    totalInQueue: (currentlyProcessingId ? 1 : 0) + queue.length,
    isCurrentlyProcessing: false,
  };
};

/**
 * Remove workflow from processing queue
 * @param {string} workflowSessionId - The workflow session ID to remove
 */
export const removeFromQueue = (workflowSessionId) => {
  const store = getStore();
  store.dispatch(removeFromProcessingQueue(workflowSessionId));
};

/**
 * Set workflow as currently processing
 * @param {string} workflowSessionId - The workflow session ID
 */
export const setCurrentlyProcessing = (workflowSessionId) => {
  const store = getStore();
  store.dispatch(setCurrentlyProcessingSession(workflowSessionId));
};

/**
 * Clear currently processing workflow
 */
export const clearCurrentlyProcessing = () => {
  const store = getStore();
  store.dispatch(setCurrentlyProcessingSession(null));
};

/**
 * Start processing for a workflow (either immediately or add to queue)
 * @param {string} workflowSessionId - The workflow session ID
 * @param {Function} startProcessingCallback - Callback to start processing if workflow can process immediately
 * @returns {Object} { canProcess: boolean, addedToQueue: boolean, message: string }
 */
export const startProcessingOrQueue = (workflowSessionId, startProcessingCallback) => {
  // REMOVED: Backend now supports multiple concurrent jobs All workflows can process immediately without queuing
  
  // Start processing immediately
  if (startProcessingCallback) {
    startProcessingCallback();
  }
  
  return {
    canProcess: true,
    addedToQueue: false,
    message: 'Processing started',
  };
};

